/**
 * The bar chart's arithmetic, apart from the SVG that draws it.
 *
 * A layout bug renders something entirely plausible - every bar 8% too short, or
 * the axis topping out one tick early - so the geometry is computed here, by pure
 * functions, and tested on numbers rather than on pixels somebody looked at.
 *
 * Three of these rules exist because of a specific way a budget chart lies:
 *
 *   - **A null total gets no bar at all** (`bar` is null), not a zero-width one.
 *     Zero width and zero value are the same picture, and "nobody has stated the
 *     flight software's mass" must not read as "the flight software weighs
 *     nothing".
 *   - **A stated zero DOES get zero width.** The example's structure draws 0 W,
 *     and that is a measurement. Zero and unset are the two states this pair of
 *     rules keeps apart, which is the whole reason the payload is nullable.
 *   - **A tiny positive value gets a visible minimum.** 0.05 kg of fasteners
 *     against a 150 kg axis is a quarter of a pixel; rounded to nothing it would
 *     join the two states above and make three different facts look identical.
 *
 * The bars are laid out with the part's NAME on its own line above each bar and
 * the value right-aligned on that same line, rather than in a left-hand gutter.
 * SVG cannot measure text, so a gutter means guessing a character budget and
 * eventually clipping a name; a full-width line cannot clip, and the numbers form
 * a readable right-hand column instead of riding bar tips of every length.
 */

import type { BudgetSlice, SliceMark } from './budget-model'
import { formatTotal, sliceMark } from './budget-model'

/**
 * The user-space box every chart is drawn in.
 *
 * Fixed, with the SVG scaled by `width: 100%` and a `viewBox`, so the geometry is
 * deterministic and testable and the picture still fits a phone.
 */
export const CHART_WIDTH = 740
/** Where a full-length bar ends; the rest is room for the last tick's label. */
export const PLOT_WIDTH = 700
export const ROW_HEIGHT = 42
export const BAR_HEIGHT = 12
/** The name and value sit on this baseline within a row. */
export const LABEL_BASELINE = 11
/** The bar's top edge within a row. */
export const BAR_TOP = 18
export const PLOT_TOP = 4
/** Tick labels, below the plot. */
export const AXIS_BAND = 24
/**
 * The shortest bar a positive value may draw.
 *
 * Not cosmetic: below about 2px a bar is indistinguishable from no bar, which is
 * what "not stated" looks like.
 */
export const MIN_BAR = 2
/** The 4px rounded data end. Square at the baseline, per the mark spec. */
export const BAR_RADIUS = 4

/**
 * A rectangle rounded on its data end only.
 *
 * Square where it meets the baseline, because a bar rounded at x=0 lifts off its
 * own zero and reads as starting slightly late. The radius is clamped to the
 * bar's own size so a 3px bar degenerates to a rectangle instead of drawing an
 * arc wider than the shape it belongs to.
 *
 * @param x left edge, in user space
 * @param y top edge
 * @param width bar length; 0 is legal and yields an empty path
 * @param height bar thickness
 * @returns an SVG path, or '' when there is nothing to draw
 */
export function barPath(x: number, y: number, width: number, height: number): string {
  if (width <= 0) return ''
  const r = Math.min(BAR_RADIUS, width, height / 2)
  const right = x + width
  const bottom = y + height
  if (r <= 0) return `M${x},${y} H${right} V${bottom} H${x} Z`
  return (
    `M${x},${y} H${right - r} A${r},${r} 0 0 1 ${right},${y + r} ` +
    `V${bottom - r} A${r},${r} 0 0 1 ${right - r},${bottom} H${x} Z`
  )
}

/** One gridline and its label. */
export interface AxisTick {
  value: number
  x: number
  label: string
}

/**
 * A clean step for an axis - 1, 2, 2.5 or 5 times a power of ten.
 *
 * @param rough the raw span divided by the tick count wanted
 */
function niceStep(rough: number): number {
  if (!(rough > 0)) return 1
  const magnitude = 10 ** Math.floor(Math.log10(rough))
  for (const factor of [1, 2, 2.5, 5, 10]) {
    if (factor * magnitude >= rough) return factor * magnitude
  }
  return 10 * magnitude
}

/**
 * The axis: rounded ticks from zero up to at least `max`.
 *
 * The top tick is the scale's maximum, so a 141.85 kg vehicle is plotted against
 * 150 and the bars stop short of the full width. That is deliberate - a
 * breakdown whose bars exactly fill the plot invites the reader to see them
 * tiling a whole, and one of them may be a subsystem that stated nothing.
 *
 * @param max the largest value to be plotted; 0 or negative yields a unit axis
 * @param wanted roughly how many ticks
 */
export function axisTicks(max: number, wanted = 4): AxisTick[] {
  const step = niceStep((max > 0 ? max : 1) / Math.max(1, wanted))
  const top = Math.max(step, Math.ceil(tidyish(max) / step) * step)
  const ticks: AxisTick[] = []
  for (let value = 0; value <= top + step / 1e6; value += step) {
    const rounded = Math.round(value * 1e6) / 1e6
    ticks.push({
      value: rounded,
      x: (rounded / top) * PLOT_WIDTH,
      label: rounded.toLocaleString('en-US', { maximumFractionDigits: 6 }),
    })
  }
  return ticks
}

/** Guard the ceiling against float noise pushing 150.0000000001 to a new tick. */
function tidyish(value: number): number {
  return Math.round(value * 1e6) / 1e6
}

/** One laid-out bar. */
export interface BarRow {
  key: string
  label: string
  /** The part's ID, or '' for the residual row. */
  id: string
  /** The value as it must read - `≥ 85.05 kg`, `0 W`, `not stated`. */
  value: string
  /** Baseline for the name and the value. */
  textY: number
  /**
   * The bar, or null when nothing was stated.
   *
   * Null rather than `{width: 0}`: a caller cannot then accidentally render an
   * unknown as a zero, because there is no rectangle to render.
   */
  bar: { x: number; y: number; width: number; height: number; path: string } | null
  mark: SliceMark
  /** True when the row's extent is unknown - drawn as a dashed rule, not a bar. */
  isUnknown: boolean
  /** The dashed rule's y, for an unknown row. */
  ruleY: number
}

/** A whole chart, ready to draw. */
export interface BarLayout {
  width: number
  height: number
  /** The top of the axis band - where the gridlines stop. */
  plotHeight: number
  axisMax: number
  ticks: AxisTick[]
  rows: BarRow[]
}

/**
 * Lay out one quantity's breakdown as horizontal bars.
 *
 * Scaled to the axis maximum rather than to the largest bar, so every quantity's
 * chart reads against a round number and a subsystem's bar length is its share
 * of the budget rather than of its biggest sibling.
 *
 * @param slices the breakdown's slices, already ordered
 * @param headline the root's total, when it has one - the scale's anchor
 */
export function barLayout(slices: BudgetSlice[], headline: number | null): BarLayout {
  const biggest = slices.reduce((most, slice) => Math.max(most, slice.total ?? 0), 0)
  // The headline anchors the scale when there is one: the slices sum to it, so
  // scaling to the biggest slice instead would stretch a 60% subsystem to the
  // full width and quietly redefine what "the whole" is.
  const ticks = axisTicks(Math.max(headline ?? 0, biggest))
  const axisMax = ticks[ticks.length - 1]?.value || 1

  const rows: BarRow[] = slices.map((slice, at) => {
    const top = PLOT_TOP + at * ROW_HEIGHT
    const mark = sliceMark(slice)
    const unknown = slice.total === null
    let bar: BarRow['bar'] = null
    if (!unknown) {
      const raw = (Math.max(0, slice.total as number) / axisMax) * PLOT_WIDTH
      // A stated zero draws nothing and says '0'; anything positive draws at
      // least MIN_BAR, so 0.05 kg is not rounded into looking like either.
      const width = (slice.total as number) <= 0 ? 0 : Math.max(MIN_BAR, raw)
      const y = top + BAR_TOP
      bar = { x: 0, y, width, height: BAR_HEIGHT, path: barPath(0, y, width, BAR_HEIGHT) }
    }
    return {
      key: slice.key,
      label: slice.label,
      id: slice.id,
      value: formatTotal({
        own: null,
        total: slice.total,
        unit: slice.unit,
        is_partial: slice.isPartial,
        unstated: slice.unstated,
        how: slice.how,
        bound: slice.bound,
        problems: slice.problems,
      }),
      textY: top + LABEL_BASELINE,
      bar,
      mark,
      isUnknown: unknown,
      ruleY: top + BAR_TOP + BAR_HEIGHT / 2,
    }
  })

  const plotHeight = PLOT_TOP + slices.length * ROW_HEIGHT
  return {
    width: CHART_WIDTH,
    height: plotHeight + AXIS_BAND,
    plotHeight,
    axisMax,
    ticks,
    rows,
  }
}
